import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import logger from "../../utils/logger";

const OAuthCallback: React.FC = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"processing" | "success" | "error">(
    "processing",
  );
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    const handleCallback = async () => {
      // Get URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const state = urlParams.get("state");
      const error = urlParams.get("error");

      // Try to parse JSON from the page body (backend might return JSON directly)
      let jsonResponse: any = null;
      try {
        // Check if the page body contains JSON (backend might return JSON as text)
        // This handles the case where backend returns JSON with Content-Type: application/json
        // and browser displays it as plain text
        const bodyText =
          document.body?.textContent?.trim() ||
          document.documentElement?.textContent?.trim();
        if (
          bodyText &&
          (bodyText.startsWith("{") || bodyText.startsWith("["))
        ) {
          try {
            jsonResponse = JSON.parse(bodyText);
          } catch (e) {
            // If parsing fails, might be HTML with JSON embedded
          }
        }

        // Also check for JSON in script tags (backend might embed it)
        if (!jsonResponse) {
          const scriptTags = document.querySelectorAll(
            'script[type="application/json"]',
          );
          if (scriptTags.length > 0) {
            jsonResponse = JSON.parse(scriptTags[0].textContent || "{}");
          }
        }

        // Check for JSON in a data attribute
        if (!jsonResponse) {
          const jsonData = document.body?.getAttribute("data-json");
          if (jsonData) {
            jsonResponse = JSON.parse(jsonData);
          }
        }

        // If we have code/state but no JSON, and we're in a popup,
        // the backend might have processed it and we need to check the response
        // Try to read from pre tag (common when JSON is displayed as text)
        if (!jsonResponse && (code || state)) {
          const preTag = document.querySelector("pre");
          if (preTag?.textContent) {
            try {
              jsonResponse = JSON.parse(preTag.textContent);
            } catch (e) {
              // Not JSON
            }
          }
        }
      } catch (e) {
        // Not JSON, continue with URL params
      }

      // Check if this is a popup callback
      if (window.opener && !window.opener.closed) {
        // Handle JSON response from backend
        if (jsonResponse) {
          const message = {
            type: "oauth_callback",
            jsonResponse,
            timestamp: Date.now(),
          };

          window.opener.postMessage(message, "*");

          // Update UI and close
          if (jsonResponse.error) {
            setStatus("error");
            setMessage(jsonResponse.error);
          } else if (jsonResponse.connection || jsonResponse.message) {
            setStatus("success");
            setMessage(
              jsonResponse.message || t("oauth.connection_established"),
            );
          }

          setTimeout(() => {
            window.close();
          }, 1500);

          return;
        }

        // Fallback to URL parameters (original flow)
        const message = {
          type: "oauth_callback",
          code,
          state,
          error,
          timestamp: Date.now(),
        };

        // Send message to parent window
        // Use '*' as targetOrigin since we're on a different domain (ngrok)
        // The parent window will verify the state parameter for security
        window.opener.postMessage(message, "*");

        // Update UI
        if (error) {
          setStatus("error");
          setMessage(error);
        } else if (code) {
          setStatus("success");
          setMessage(t("oauth.connection_established"));
        }

        // Close popup after a short delay to show success message
        setTimeout(() => {
          window.close();
        }, 1500);

        return;
      }

      // Handle full page redirect callback
      if (jsonResponse) {
        if (jsonResponse.error) {
          setStatus("error");
          setMessage(jsonResponse.error);
          localStorage.setItem("oauth_error", jsonResponse.error);
        } else if (jsonResponse.connection || jsonResponse.message) {
          setStatus("success");
          setMessage(jsonResponse.message || t("oauth.connection_established"));
          localStorage.setItem("oauth_success", JSON.stringify(jsonResponse));
        }

        setTimeout(() => {
          window.location.href = "/connections";
        }, 2000);

        return;
      }

      // Handle URL parameter-based callback (original flow)
      if (error) {
        // Handle OAuth error
        logger.error("OAuth error:", error);
        setStatus("error");
        setMessage(error);

        // Store error for the main app to display
        localStorage.setItem("oauth_error", error);

        // Redirect back to connections page
        setTimeout(() => {
          window.location.href = "/connections";
        }, 2000);

        return;
      }

      if (code && state) {
        // Check if this is a popup callback
        if (window.opener && !window.opener.closed) {
          // Send message to parent window with code/state
          // The parent will handle the OAuth flow completion
          const message = {
            type: "oauth_callback",
            code,
            state,
            error: null,
            timestamp: Date.now(),
          };

          window.opener.postMessage(message, "*");

          setStatus("success");
          setMessage(t("oauth.connection_established"));

          // Close popup after a short delay
          setTimeout(() => {
            window.close();
          }, 1500);
        } else {
          // Full page redirect
          setStatus("success");
          setMessage(t("oauth.connection_established"));

          // Store success parameters for the main app to process
          localStorage.setItem(
            "oauth_success",
            JSON.stringify({
              code,
              state,
              timestamp: Date.now(),
            }),
          );

          // Redirect back to connections page
          setTimeout(() => {
            window.location.href = "/connections";
          }, 1500);
        }

        return;
      }

      // Invalid callback - no code or error
      setStatus("error");
      setMessage(t("oauth.invalid_callback_parameters"));
      localStorage.setItem(
        "oauth_error",
        t("oauth.invalid_callback_parameters"),
      );

      setTimeout(() => {
        window.location.href = "/connections";
      }, 2000);
    };

    handleCallback();
  }, [t]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        bgcolor: "background.default",
        p: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          maxWidth: 400,
        }}
      >
        {status === "error" ? (
          <>
            <ErrorIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {t("oauth.authorization_failed")}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {message || t("oauth.authorization_failed_error", { error: "" })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("oauth.redirecting_back")}
            </Typography>
          </>
        ) : status === "success" ? (
          <>
            <CheckCircleIcon
              sx={{ fontSize: 64, color: "success.main", mb: 2 }}
            />
            <Typography variant="h5" gutterBottom>
              {t("oauth.authorization_successful")}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {message || t("oauth.connection_established")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("oauth.redirecting_back")}
            </Typography>
          </>
        ) : (
          <>
            <CircularProgress size={64} sx={{ mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {t("oauth.processing_authorization")}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {t("oauth.please_wait_connection")}
            </Typography>
          </>
        )}

        {/* Loading indicator */}
        {status !== "processing" && (
          <Box sx={{ mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OAuthCallback;
